import { useEffect, useState } from 'react';

import { BankOutlined, LockOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import { ApiErrorData } from 'models/ApiResponse';
import { Country } from 'models/Country';
import { KitCompany } from 'models/KitCompany';
import {
  TiendaPerfectaCompaniesResponse,
  TiendaPerfectaCompany,
} from 'models/TiendaPerfecta';

import { KitCompanyService } from 'services/KitCompanyService';
import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { CompanyFormPayload } from '../types';
import KitIntegrationFields, {
  KIT_COMPANIES_FIELD,
} from './KitIntegrationFields';
import TiendaPerfectaIntegrationFields, {
  TIENDA_PERFECTA_COMPANY_FIELD,
} from './TiendaPerfectaIntegrationFields';

const I18N_BASE = 'scenes.settings.companies.components.new_company_modal';

interface NewCompanyModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (payload: CompanyFormPayload & { password: string }) => void;
  countries: Country[];
}

function NewCompanyModal({
  isOpen,
  handleModalCancel,
  submit,
  countries,
}: NewCompanyModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordReadOnly, setPasswordReadOnly] = useState(true);
  const [kitEnabled, setKitEnabled] = useState(false);
  const [tiendaPerfectaEnabled, setTiendaPerfectaEnabled] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [kitCompanies, setKitCompanies] = useState<KitCompany[]>([]);
  const [kitCompaniesLoading, setKitCompaniesLoading] = useState(false);
  const [tiendaPerfectaCompanies, setTiendaPerfectaCompanies] = useState<
    TiendaPerfectaCompany[]
  >([]);
  const [tiendaPerfectaCompaniesLoading, setTiendaPerfectaCompaniesLoading] =
    useState(false);
  const { t } = useTranslation();

  const selectedCountry = Form.useWatch('country', form);
  const selectedKitCompanyIds = Form.useWatch<number[]>(
    KIT_COMPANIES_FIELD,
    form
  );
  const selectedTiendaPerfectaCode = Form.useWatch<string>(
    TIENDA_PERFECTA_COMPANY_FIELD,
    form
  );

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setPasswordReadOnly(true);
      setKitEnabled(false);
      setTiendaPerfectaEnabled(false);
      setIsPrivate(false);
      setKitCompanies([]);
    }
  }, [isOpen]);

  // El catálogo de Tienda Perfecta no depende del país, así que se pide una
  // sola vez al abrir el modal.
  useEffect(() => {
    if (!isOpen) return;

    const fetchTiendaPerfectaCompanies = async () => {
      setTiendaPerfectaCompaniesLoading(true);

      const response = await TiendaPerfectaService.getCompanies();

      if (response.success) {
        setTiendaPerfectaCompanies(
          (response.data as TiendaPerfectaCompaniesResponse)?.companies ?? []
        );
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.settings.companies.scene.errors.fetch_tienda_perfecta_companies'
            ),
          3.5
        );
      }

      setTiendaPerfectaCompaniesLoading(false);
    };

    fetchTiendaPerfectaCompanies();
  }, [isOpen]);

  const handleCountryChange = async (countryCode: string) => {
    form.setFieldValue(KIT_COMPANIES_FIELD, []);
    setKitCompanies([]);
    setKitCompaniesLoading(true);

    const response = await KitCompanyService.getByCountry(countryCode);

    if (response.success) {
      setKitCompanies(response.data as KitCompany[]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.fetch_kit_companies'),
        3.5
      );
    }

    setKitCompaniesLoading(false);
  };

  const handleKitSwitchChange = (checked: boolean) => {
    setKitEnabled(checked);
    if (!checked) {
      form.setFieldValue(KIT_COMPANIES_FIELD, []);
    }
  };

  const handleTiendaPerfectaSwitchChange = (checked: boolean) => {
    setTiendaPerfectaEnabled(checked);
    if (!checked) {
      form.setFieldValue(TIENDA_PERFECTA_COMPANY_FIELD, undefined);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit({
      name: values.name.trim(),
      countryCode: values.country,
      password: values.password,
      isPrivate,
      kitIntegrationEnabled: kitEnabled,
      kitCompanyIds: kitEnabled ? values[KIT_COMPANIES_FIELD] ?? [] : [],
      tiendaPerfectaIntegrationEnabled: tiendaPerfectaEnabled,
      tiendaPerfectaCompanyCode: tiendaPerfectaEnabled
        ? values[TIENDA_PERFECTA_COMPANY_FIELD] ?? null
        : null,
    });

    setLoading(false);
  };

  const handleRemoveReadOnly = () => {
    setPasswordReadOnly(false);
  };

  return (
    <Modal
      title={t(`${I18N_BASE}.title`)}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={560}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <IntegrationCard
          title={t(`${I18N_BASE}.sections.company_data`)}
          icon={<BankOutlined />}
        >
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="name"
                label={t(`${I18N_BASE}.labels.name`)}
                style={{ marginBottom: 16 }}
                rules={[
                  {
                    required: true,
                    message: t(`${I18N_BASE}.validations.name.required`),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(`${I18N_BASE}.placeholders.name`)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="country"
                label={t(`${I18N_BASE}.labels.country`)}
                style={{ marginBottom: 16 }}
                rules={[
                  {
                    required: true,
                    message: t(`${I18N_BASE}.validations.country.required`),
                  },
                ]}
              >
                <Select
                  placeholder={t(`${I18N_BASE}.placeholders.select_option`)}
                  options={countries.map((record) => ({
                    value: record.code,
                    label: record.name,
                  }))}
                  disabled={loading}
                  onChange={handleCountryChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Switch
                  checked={isPrivate}
                  onChange={(checked) => setIsPrivate(checked)}
                  disabled={loading}
                />
                <span style={{ marginLeft: 10 }}>
                  {t(`${I18N_BASE}.labels.is_private_switch`)}
                </span>
              </Form.Item>
            </Col>
          </Row>
        </IntegrationCard>

        <KitIntegrationFields
          enabled={kitEnabled}
          onEnabledChange={handleKitSwitchChange}
          kitCompanies={kitCompanies}
          kitCompaniesLoading={kitCompaniesLoading}
          disabled={loading}
          selectDisabled={!selectedCountry}
          selectedCompanyIds={selectedKitCompanyIds ?? []}
        />

        <TiendaPerfectaIntegrationFields
          enabled={tiendaPerfectaEnabled}
          onEnabledChange={handleTiendaPerfectaSwitchChange}
          companies={tiendaPerfectaCompanies}
          companiesLoading={tiendaPerfectaCompaniesLoading}
          disabled={loading}
          selectedCompanyCode={selectedTiendaPerfectaCode ?? null}
        />

        <IntegrationCard
          title={t(`${I18N_BASE}.sections.encrypted_reports`)}
          icon={<LockOutlined />}
        >
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="password"
                label={t(`${I18N_BASE}.labels.password`)}
                style={{ marginBottom: 16 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          new Error(
                            t(`${I18N_BASE}.validations.password.required`)
                          )
                        );
                      }
                      if (value.length < 8) {
                        return Promise.reject(
                          new Error(
                            t(`${I18N_BASE}.validations.password.min_length`)
                          )
                        );
                      }
                      if (!/[A-Za-z]/.test(value)) {
                        return Promise.reject(
                          new Error(
                            t(
                              `${I18N_BASE}.validations.password.letter_required`
                            )
                          )
                        );
                      }
                      if (!/[0-9]/.test(value)) {
                        return Promise.reject(
                          new Error(
                            t(
                              `${I18N_BASE}.validations.password.number_required`
                            )
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  disabled={loading}
                  placeholder={t(`${I18N_BASE}.placeholders.password`)}
                  autoComplete="off"
                  readOnly={passwordReadOnly}
                  onFocus={handleRemoveReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="passwordConfirm"
                label={t(`${I18N_BASE}.labels.password_confirm`)}
                dependencies={['password']}
                style={{ marginBottom: 0 }}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error(
                            t(
                              `${I18N_BASE}.validations.password_confirm.required`
                            )
                          )
                        );
                      }
                      if (value !== getFieldValue('password')) {
                        return Promise.reject(
                          new Error(
                            t(`${I18N_BASE}.validations.password_confirm.match`)
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.Password
                  disabled={loading}
                  placeholder={t(`${I18N_BASE}.placeholders.password_confirm`)}
                  autoComplete="off"
                  readOnly={passwordReadOnly}
                  onFocus={handleRemoveReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>
        </IntegrationCard>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(`${I18N_BASE}.buttons.save_changes`)}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default NewCompanyModal;
